import type { TripStatus, VehicleType } from '@raahi/shared';

interface DemoTrip {
  id: string;
  status: TripStatus;
  vehicleType: VehicleType;
  pickup: { lat: number; lng: number; label: string };
  drop: { lat: number; lng: number; label: string };
  distanceKm: number;
  durationMin: number;
  fare: { base: number; distance: number; duration: number; surge: number; total: number };
  paymentMethod: string;
  driver?: { id: string };
}

interface OfferTemplate {
  pickup: { lat: number; lng: number; label: string };
  drop: { lat: number; lng: number; label: string };
  distanceKm: number;
  estimatedFare: number;
}

const OFFER_TEMPLATES: OfferTemplate[] = [
  {
    pickup: { lat: 24.8607, lng: 67.0011, label: 'Saddar' },
    drop: { lat: 24.8138, lng: 67.0299, label: 'Clifton' },
    distanceKm: 6.4,
    estimatedFare: 718,
  },
  {
    pickup: { lat: 24.8779, lng: 67.0671, label: 'Dolmen Mall Tariq Road' },
    drop: { lat: 24.9008, lng: 67.1681, label: 'Jinnah International Airport' },
    distanceKm: 14.2,
    estimatedFare: 2175,
  },
  {
    pickup: { lat: 24.8949, lng: 67.0775, label: 'Gulshan Block 6' },
    drop: { lat: 24.9056, lng: 67.0822, label: 'Gulshan Block 10' },
    distanceKm: 1.8,
    estimatedFare: 270,
  },
];

type Handler = (payload: unknown) => void;

class DemoDriverState {
  online = false;

  activeTrip: DemoTrip | null = null;

  private earnings = { today: 1245, week: 8970, lifetime: 184_500 };

  private recent: Array<{ id: string; fareTotal: number; endedAt: string; pickupLabel: string; dropLabel: string }> = [
    { id: 'd-trip-1', fareTotal: 718, endedAt: hoursAgo(2), pickupLabel: 'Saddar', dropLabel: 'Clifton' },
    { id: 'd-trip-2', fareTotal: 527, endedAt: hoursAgo(5), pickupLabel: 'Gulshan Block 6', dropLabel: 'Sea View' },
    { id: 'd-trip-3', fareTotal: 1402, endedAt: daysAgo(1), pickupLabel: 'Tariq Road', dropLabel: 'Airport' },
  ];

  private listeners = new Map<string, Set<Handler>>();

  private offerTimer: ReturnType<typeof setTimeout> | null = null;

  private stageTimers: ReturnType<typeof setTimeout>[] = [];

  on(event: string, handler: Handler): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(handler);
    return () => set?.delete(handler);
  }

  private emit(event: string, payload: unknown): void {
    this.listeners.get(event)?.forEach((fn) => fn(payload));
  }

  setStatus(online: boolean): void {
    this.online = online;
    if (online && !this.activeTrip) this.armNextOffer(2500);
    else this.clearOfferTimer();
  }

  earningsSnapshot() {
    return { ...this.earnings, recent: this.recent };
  }

  getActiveTrip(): DemoTrip | null {
    return this.activeTrip;
  }

  acceptOffer(tripId: string): void {
    if (!this.activeTrip || this.activeTrip.id !== tripId) {
      this.activeTrip = this.materialiseOffer(tripId);
    }
    this.move('assigned');
    this.stageTimers.push(setTimeout(() => this.move('arriving'), 400));
  }

  declineOffer(): void {
    this.armNextOffer(4000);
  }

  markArrived(): void {
    this.move('arrived');
  }

  startTrip(): void {
    this.move('in_progress');
  }

  completeTrip(): void {
    this.move('completed');
    const trip = this.activeTrip;
    if (trip) {
      this.earnings.today += trip.fare.total;
      this.earnings.week += trip.fare.total;
      this.earnings.lifetime += trip.fare.total;
      this.recent = [
        {
          id: trip.id,
          fareTotal: trip.fare.total,
          endedAt: new Date().toISOString(),
          pickupLabel: trip.pickup.label,
          dropLabel: trip.drop.label,
        },
        ...this.recent,
      ].slice(0, 10);
    }
    for (const t of this.stageTimers) clearTimeout(t);
    this.stageTimers = [];
    this.activeTrip = null;
    if (this.online) this.armNextOffer(6000);
  }

  private armNextOffer(delay: number): void {
    this.clearOfferTimer();
    if (!this.online || this.activeTrip) return;
    this.offerTimer = setTimeout(() => {
      if (!this.online || this.activeTrip) return;
      const template = OFFER_TEMPLATES[Math.floor((Date.now() / 1000) % OFFER_TEMPLATES.length)]!;
      const tripId = `demo-offer-${Date.now()}`;
      this.emit('trip:offer', {
        tripId,
        pickup: { lat: template.pickup.lat, lng: template.pickup.lng },
        drop: { lat: template.drop.lat, lng: template.drop.lng },
        distanceKm: template.distanceKm,
        estimatedFare: template.estimatedFare,
        expiresAt: new Date(Date.now() + 15_000).toISOString(),
      });
      this.activeTrip = this.materialiseOffer(tripId, template);
    }, delay);
  }

  private clearOfferTimer(): void {
    if (this.offerTimer) {
      clearTimeout(this.offerTimer);
      this.offerTimer = null;
    }
  }

  private materialiseOffer(tripId: string, template?: OfferTemplate): DemoTrip {
    const t = template ?? OFFER_TEMPLATES[0]!;
    const fareTotal = t.estimatedFare;
    return {
      id: tripId,
      status: 'requested',
      vehicleType: 'economy',
      pickup: t.pickup,
      drop: t.drop,
      distanceKm: t.distanceKm,
      durationMin: Math.max(1, Math.round((t.distanceKm / 28) * 60)),
      fare: {
        base: 150,
        distance: Math.round(fareTotal * 0.55),
        duration: Math.round(fareTotal * 0.25),
        surge: 0,
        total: fareTotal,
      },
      paymentMethod: 'cash',
      driver: { id: 'demo-driver-profile-imran' },
    };
  }

  private move(to: TripStatus): void {
    if (!this.activeTrip) return;
    this.activeTrip = { ...this.activeTrip, status: to };
    this.emit('trip:update', {
      tripId: this.activeTrip.id,
      status: to,
      at: new Date().toISOString(),
    });
  }
}

function hoursAgo(h: number): string {
  return new Date(Date.now() - h * 60 * 60 * 1000).toISOString();
}
function daysAgo(d: number): string {
  return new Date(Date.now() - d * 24 * 60 * 60 * 1000).toISOString();
}

export const driverDemoState = new DemoDriverState();
